import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format, parseISO, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, isToday, isBefore, startOfDay, getDay } from "date-fns";
import { Calendar, Clock, User, MapPin, ArrowRight, Check, ChevronLeft, ChevronRight, Sparkles, Building2, Mail, Phone, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Premium animated background
const AnimatedBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    {/* Base gradient */}
    <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#111111] to-[#0a0a0a]" />
    
    {/* Animated gradient orbs */}
    <motion.div
      className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full"
      style={{
        background: "radial-gradient(circle, rgba(255,107,53,0.08) 0%, transparent 70%)",
        filter: "blur(60px)"
      }}
      animate={{
        x: [0, 50, 0],
        y: [0, 30, 0],
        scale: [1, 1.1, 1]
      }}
      transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full"
      style={{
        background: "radial-gradient(circle, rgba(255,107,53,0.05) 0%, transparent 70%)",
        filter: "blur(80px)"
      }}
      animate={{
        x: [0, -40, 0],
        y: [0, -40, 0],
        scale: [1, 1.15, 1]
      }}
      transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 2 }}
    />
    
    {/* Noise texture overlay */}
    <div className="absolute inset-0 opacity-[0.015]" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
    }} />
  </div>
);

// Premium calendar component
const PremiumCalendar = ({ availableSlots, selectedDate, onSelectDate }) => {
  const [currentMonth, setCurrentMonth] = useState(() => new Date()); // Initialize with current date
  
  // Get available dates from slots
  const availableDates = availableSlots.map(slot => slot.date);
  
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const startDate = addDays(monthStart, -getDay(monthStart));
  const endDate = addDays(monthEnd, 6 - getDay(monthEnd));
  
  const days = eachDayOfInterval({ start: startDate, end: endDate });
  const weekDays = ["S", "M", "T", "W", "T", "F", "S"];

  const isDateAvailable = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return availableDates.includes(dateStr) && !isBefore(date, startOfDay(new Date()));
  };

  return (
    <motion.div 
      className="bg-[#0f0f0f] rounded-2xl p-6 border border-[#1a1a1a]"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-6">
        <motion.h3 
          key={format(currentMonth, "MMMM yyyy")}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-xl font-light text-white tracking-wide"
        >
          {format(currentMonth, "MMMM")}
        </motion.h3>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentMonth(addDays(monthStart, -1))}
            className="p-2 rounded-full hover:bg-[#1a1a1a] transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-[#666]" />
          </button>
          <button
            onClick={() => setCurrentMonth(addDays(monthEnd, 1))}
            className="p-2 rounded-full hover:bg-[#1a1a1a] transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-[#666]" />
          </button>
        </div>
      </div>

      {/* Week Days */}
      <div className="grid grid-cols-7 mb-2">
        {weekDays.map((day, i) => (
          <div key={i} className="text-center text-xs text-[#555] py-2 font-medium">
            {day}
          </div>
        ))}
      </div>

      {/* Days Grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, i) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const isAvailable = isDateAvailable(day);
          const isSelected = selectedDate === dateStr;
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isPast = isBefore(day, startOfDay(new Date()));

          return (
            <motion.button
              key={i}
              onClick={() => isAvailable && onSelectDate(dateStr)}
              disabled={!isAvailable}
              className={`
                relative aspect-square flex items-center justify-center rounded-xl text-sm font-medium transition-all
                ${!isCurrentMonth ? "text-[#333]" : ""}
                ${isPast && isCurrentMonth ? "text-[#333]" : ""}
                ${isCurrentMonth && !isPast && !isAvailable ? "text-[#444]" : ""}
                ${isAvailable && !isSelected ? "text-white hover:bg-[#1a1a1a] cursor-pointer" : ""}
                ${isSelected ? "text-black" : ""}
                ${isToday(day) && !isSelected ? "ring-1 ring-[#333]" : ""}
              `}
              whileHover={isAvailable ? { scale: 1.1 } : {}}
              whileTap={isAvailable ? { scale: 0.95 } : {}}
            >
              {isSelected && (
                <motion.div
                  layoutId="selectedDate"
                  className="absolute inset-1 bg-[#FF6B35] rounded-xl"
                  initial={false}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
              <span className="relative z-10">{format(day, "d")}</span>
              {isAvailable && !isSelected && (
                <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#FF6B35]" />
              )}
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
};

// Time slot component
const TimeSlotPicker = ({ slots, selectedDate, selectedTime, onSelectTime }) => {
  const availableSlots = slots.filter(s => s.date === selectedDate);

  if (!selectedDate) {
    return (
      <motion.div 
        className="bg-[#0f0f0f] rounded-2xl p-6 border border-[#1a1a1a] flex items-center justify-center min-h-[200px]"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <p className="text-[#555] text-center">Select a date to see available times</p>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="bg-[#0f0f0f] rounded-2xl p-6 border border-[#1a1a1a]"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
    >
      <h3 className="text-lg font-light text-white mb-4 flex items-center gap-2">
        <Clock className="w-4 h-4 text-[#FF6B35]" />
        Available Times
      </h3>
      
      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {availableSlots.map((slot, i) => (
            <motion.button
              key={`${slot.date}-${slot.start_time}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => onSelectTime(slot.start_time)}
              className={`
                w-full px-4 py-3 rounded-xl text-left transition-all flex items-center justify-between
                ${selectedTime === slot.start_time 
                  ? "bg-[#FF6B35] text-black" 
                  : "bg-[#1a1a1a] text-white hover:bg-[#222]"}
              `}
            >
              <span className="font-medium">
                {slot.start_time} - {slot.end_time}
              </span>
              {selectedTime === slot.start_time && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500 }}
                >
                  <Check className="w-4 h-4" />
                </motion.div>
              )}
            </motion.button>
          ))}
        </AnimatePresence>
      </div>

      {availableSlots.length === 0 && (
        <p className="text-[#555] text-center py-4">No slots available for this date</p>
      )}
    </motion.div>
  );
};

export default function BookAppointment() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [step, setStep] = useState(0); // 0: Guest info (if anonymous), 1: Select time, 2: Enter details, 3: Confirmed
  const [isAnonymousLink, setIsAnonymousLink] = useState(false);
  
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  
  const [formData, setFormData] = useState({
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    guest_company: "",
    notes: ""
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  const fetchBookingData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/book/${linkId}/public`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Booking link not found");
      }
      const data = await response.json();
      setBookingData(data);
      
      // Check if this is an anonymous link (no guest name provided)
      if (data.link.guest_name) {
        setFormData(prev => ({
          ...prev,
          guest_name: data.link.guest_name,
          guest_email: data.link.guest_email || "",
          guest_phone: data.link.guest_phone || ""
        }));
        setStep(1); // Skip to time selection
        setIsAnonymousLink(false);
      } else {
        // Anonymous link - need to collect guest info first
        setStep(0);
        setIsAnonymousLink(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [linkId]);

  useEffect(() => {
    fetchBookingData();
  }, [fetchBookingData]);

  const handleGuestInfoSubmit = () => {
    if (!formData.guest_name || !formData.guest_email) {
      toast.error("Please enter your name and email");
      return;
    }
    setStep(1); // Move to time selection
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Please select a date and time");
      return;
    }
    
    if (!formData.guest_name || !formData.guest_email) {
      toast.error("Please enter your name and email");
      return;
    }

    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/book/${linkId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          guest_name: formData.guest_name,
          guest_email: formData.guest_email,
          guest_phone: formData.guest_phone,
          guest_company: formData.guest_company,
          selected_date: selectedDate,
          selected_time: selectedTime,
          notes: formData.notes
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to book appointment");
      }

      const result = await response.json();
      setBookingResult(result);
      setStep(3);
      toast.success("Appointment booked successfully!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <AnimatedBackground />
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <AnimatedBackground />
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#1a1a1a] flex items-center justify-center">
            <Calendar className="w-8 h-8 text-[#555]" />
          </div>
          <h1 className="text-2xl font-light text-white mb-2">Link Unavailable</h1>
          <p className="text-[#666] mb-6">{error}</p>
          <Button 
            onClick={() => window.location.reload()}
            className="bg-[#FF6B35] hover:bg-[#e55a2b]"
          >
            Try Again
          </Button>
        </motion.div>
      </div>
    );
  }

  const { link, team_member, project } = bookingData;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <AnimatedBackground />
      
      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Header */}
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Logo */}
          <motion.div 
            className="mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <span className="text-3xl md:text-4xl font-serif tracking-tight text-white">
              Tat atelier<span className="text-[#FF6B35]">.</span>
            </span>
            <p className="text-[10px] md:text-xs tracking-[0.3em] text-[#666] uppercase mt-1">
              The architecture tale
            </p>
          </motion.div>

          {/* Greeting */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="text-2xl md:text-4xl font-light text-white mb-2">
              {step === 0 ? (
                <>Welcome<span className="text-[#FF6B35]">!</span></>
              ) : (
                <>Hello, <span className="text-[#FF6B35]">{formData.guest_name || link.guest_name}</span></>
              )}
            </h1>
            <p className="text-[#888] text-sm md:text-base max-w-md mx-auto">
              {step === 0 && "Please introduce yourself to get started"}
              {step === 1 && "Select a convenient time for your meeting"}
              {step === 2 && "Confirm your details"}
              {step === 3 && "Your meeting is confirmed"}
            </p>
          </motion.div>
        </motion.div>

        {/* Meeting Info Card - hide on step 0 */}
        {step > 0 && (
        <motion.div 
          className="max-w-2xl mx-auto mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <div className="bg-[#0f0f0f]/80 backdrop-blur-sm rounded-2xl border border-[#1a1a1a] p-5 flex flex-wrap gap-4 md:gap-6 justify-center">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-8 h-8 rounded-full bg-[#1a1a1a] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[#FF6B35]" />
              </div>
              <div>
                <p className="text-[#666] text-xs">Meeting Type</p>
                <p className="text-white font-medium">{link.meeting_type}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-sm">
              <div className="w-8 h-8 rounded-full bg-[#1a1a1a] flex items-center justify-center">
                <Clock className="w-4 h-4 text-[#FF6B35]" />
              </div>
              <div>
                <p className="text-[#666] text-xs">Duration</p>
                <p className="text-white font-medium">{link.duration_minutes} min</p>
              </div>
            </div>
            
            {team_member && (
              <div className="flex items-center gap-2 text-sm">
                <div className="w-8 h-8 rounded-full bg-[#1a1a1a] flex items-center justify-center">
                  <User className="w-4 h-4 text-[#FF6B35]" />
                </div>
                <div>
                  <p className="text-[#666] text-xs">Meeting With</p>
                  <p className="text-white font-medium">{team_member.name}</p>
                </div>
              </div>
            )}
            
            {project && (
              <div className="flex items-center gap-2 text-sm">
                <div className="w-8 h-8 rounded-full bg-[#1a1a1a] flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-[#FF6B35]" />
                </div>
                <div>
                  <p className="text-[#666] text-xs">Project</p>
                  <p className="text-white font-medium">{project.name}</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
        )}

        {/* Step Progress - only show for steps 1-3 */}
        {step > 0 && (
        <motion.div 
          className="max-w-xl mx-auto mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-center gap-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all
                  ${step >= s ? "bg-[#FF6B35] text-black" : "bg-[#1a1a1a] text-[#666]"}
                `}>
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 md:w-20 h-0.5 mx-1 transition-colors ${step > s ? "bg-[#FF6B35]" : "bg-[#1a1a1a]"}`} />
                )}
              </div>
            ))}
          </div>
        </motion.div>
        )}

        {/* Content */}
        <AnimatePresence mode="wait">
          {/* Step 0: Guest Info Collection (for anonymous links) */}
          {step === 0 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-lg mx-auto"
            >
              <div className="bg-[#0f0f0f] rounded-2xl p-6 md:p-8 border border-[#1a1a1a] space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl text-white font-light mb-2">Let&apos;s get to know you</h2>
                  <p className="text-[#666] text-sm">This helps us personalize your booking experience</p>
                </div>

                {/* Form fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-[#888] mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      Your Name *
                    </label>
                    <Input
                      value={formData.guest_name}
                      onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                      placeholder="Enter your full name"
                      className="bg-[#1a1a1a] border-[#262626] h-12 text-white placeholder:text-[#555]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[#888] mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      value={formData.guest_email}
                      onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                      placeholder="your@email.com"
                      className="bg-[#1a1a1a] border-[#262626] h-12 text-white placeholder:text-[#555]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[#888] mb-2">
                      <Phone className="w-4 h-4 inline mr-2" />
                      Phone Number
                    </label>
                    <Input
                      value={formData.guest_phone}
                      onChange={(e) => setFormData({ ...formData, guest_phone: e.target.value })}
                      placeholder="+91 XXXXX XXXXX"
                      className="bg-[#1a1a1a] border-[#262626] h-12 text-white placeholder:text-[#555]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[#888] mb-2">
                      <Building2 className="w-4 h-4 inline mr-2" />
                      Company (Optional)
                    </label>
                    <Input
                      value={formData.guest_company}
                      onChange={(e) => setFormData({ ...formData, guest_company: e.target.value })}
                      placeholder="Your company name"
                      className="bg-[#1a1a1a] border-[#262626] h-12 text-white placeholder:text-[#555]"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleGuestInfoSubmit}
                  disabled={!formData.guest_name || !formData.guest_email}
                  className="w-full h-12 bg-[#FF6B35] hover:bg-[#e55a2b] text-base"
                >
                  Continue to Schedule
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-4xl mx-auto"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <PremiumCalendar 
                  availableSlots={link.available_slots || []}
                  selectedDate={selectedDate}
                  onSelectDate={setSelectedDate}
                />
                <TimeSlotPicker 
                  slots={link.available_slots || []}
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  onSelectTime={setSelectedTime}
                />
              </div>

              <motion.div 
                className="mt-8 flex justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <Button
                  onClick={() => selectedDate && selectedTime && setStep(2)}
                  disabled={!selectedDate || !selectedTime}
                  className="bg-[#FF6B35] hover:bg-[#e55a2b] px-8 py-6 text-base disabled:opacity-50"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-lg mx-auto"
            >
              <div className="bg-[#0f0f0f] rounded-2xl p-6 md:p-8 border border-[#1a1a1a] space-y-6">
                {/* Selected time summary */}
                <div className="bg-[#1a1a1a] rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="text-[#666] text-xs mb-1">Your selected time</p>
                    <p className="text-white font-medium">
                      {selectedDate && format(parseISO(selectedDate), "EEEE, MMMM d, yyyy")}
                    </p>
                    <p className="text-[#FF6B35] text-sm">{selectedTime}</p>
                  </div>
                  <button
                    onClick={() => setStep(1)}
                    className="text-sm text-[#666] hover:text-white transition-colors"
                  >
                    Change
                  </button>
                </div>

                {/* Form fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-[#888] mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      Your Name *
                    </label>
                    <Input
                      value={formData.guest_name}
                      onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                      placeholder="Enter your name"
                      className="bg-[#1a1a1a] border-[#262626] h-12 text-white placeholder:text-[#555]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[#888] mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      value={formData.guest_email}
                      onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                      placeholder="your@email.com"
                      className="bg-[#1a1a1a] border-[#262626] h-12 text-white placeholder:text-[#555]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[#888] mb-2">
                      <Phone className="w-4 h-4 inline mr-2" />
                      Phone Number
                    </label>
                    <Input
                      value={formData.guest_phone}
                      onChange={(e) => setFormData({ ...formData, guest_phone: e.target.value })}
                      placeholder="+91 XXXXX XXXXX"
                      className="bg-[#1a1a1a] border-[#262626] h-12 text-white placeholder:text-[#555]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[#888] mb-2">
                      <MessageSquare className="w-4 h-4 inline mr-2" />
                      Notes (optional)
                    </label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Anything you'd like us to know beforehand?"
                      className="bg-[#1a1a1a] border-[#262626] text-white placeholder:text-[#555] min-h-[100px]"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-12 border-[#262626] text-[#888] hover:text-white"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || !formData.guest_name || !formData.guest_email}
                    className="flex-1 h-12 bg-[#FF6B35] hover:bg-[#e55a2b]"
                  >
                    {submitting ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-black border-t-transparent rounded-full"
                      />
                    ) : (
                      <>
                        Confirm Booking
                        <Check className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-lg mx-auto text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#FF6B35] flex items-center justify-center"
              >
                <Check className="w-10 h-10 text-black" />
              </motion.div>

              <h2 className="text-2xl md:text-3xl font-light text-white mb-2">
                You&apos;re all set!
              </h2>
              <p className="text-[#888] mb-8">
                A confirmation email has been sent to <span className="text-white">{formData.guest_email}</span>
              </p>

              <div className="bg-[#0f0f0f] rounded-2xl p-6 border border-[#1a1a1a] text-left space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-[#FF6B35]" />
                  <div>
                    <p className="text-[#666] text-xs">Date</p>
                    <p className="text-white">{selectedDate && format(parseISO(selectedDate), "EEEE, MMMM d, yyyy")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-[#FF6B35]" />
                  <div>
                    <p className="text-[#666] text-xs">Time</p>
                    <p className="text-white">{selectedTime}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-[#FF6B35]" />
                  <div>
                    <p className="text-[#666] text-xs">Meeting Type</p>
                    <p className="text-white">{link.meeting_type}</p>
                  </div>
                </div>
                {team_member && (
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-[#FF6B35]" />
                    <div>
                      <p className="text-[#666] text-xs">Meeting With</p>
                      <p className="text-white">{team_member.name}</p>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-[#555] text-sm mt-8">
                Looking forward to meeting you!
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Notes display if present */}
        {step === 1 && link.notes && (
          <motion.div 
            className="max-w-2xl mx-auto mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <div className="bg-[#0f0f0f]/50 rounded-xl p-4 border border-[#1a1a1a]">
              <p className="text-[#666] text-xs mb-1">Notes from the organizer</p>
              <p className="text-[#888] text-sm">{link.notes}</p>
            </div>
          </motion.div>
        )}

        {/* Footer */}
        <motion.footer 
          className="text-center mt-12 text-[#444] text-xs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <p>Powered by TAT Atelier CRM</p>
        </motion.footer>
      </div>
    </div>
  );
}
